import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DriftFindingDto,
  OrphanedResourceDto,
  SyncHookInfoDto,
  SyncRunDto,
  SyncRunStats,
  SyncStatusDto,
} from '@cpm/shared';
import { Check, Copy, Ghost, RefreshCw, SearchCheck, Webhook } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { HelpLink } from '../components/help.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
  statusTone,
} from '../components/ui.js';

const STAT_LABELS: Array<{ key: keyof SyncRunStats; label: string }> = [
  { key: 'resourcesSeen', label: 'Resources seen' },
  { key: 'deploymentsSeen', label: 'Deployments in flight' },
  { key: 'statusUpdated', label: 'Statuses updated' },
  { key: 'orphaned', label: 'Orphaned' },
  { key: 'relinked', label: 'Relinked' },
  { key: 'findingsOpened', label: 'Findings opened' },
  { key: 'findingsResolved', label: 'Findings resolved' },
  { key: 'healthChanged', label: 'Health changes' },
];

function LastRunCard({ status }: { status: SyncStatusDto }) {
  const run = status.lastRun;
  return (
    <Card>
      <CardHeader
        title="Last run"
        description={`Runs every ${Math.round(status.intervalMs / 1000)}s, plus manual and webhook triggers`}
        actions={
          status.running ? (
            <Badge tone="blue">running…</Badge>
          ) : run ? (
            <Badge tone={statusTone(run.status)}>{run.status}</Badge>
          ) : null
        }
      />
      {!run ? (
        <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
          No sync has run yet.
        </p>
      ) : (
        <div className="space-y-3 px-5 py-4 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-slate-500 dark:text-slate-400">Started</p>
              <p className="font-medium">{new Date(run.startedAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Duration</p>
              <p className="font-medium tabular-nums">
                {run.durationMs === null ? '—' : `${run.durationMs} ms`}
              </p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Trigger</p>
              <p className="font-medium capitalize">{run.trigger}</p>
            </div>
          </div>
          {run.error && <ErrorNote error={new Error(run.error)} />}
          {run.stats && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-100 pt-3 sm:grid-cols-4 dark:border-slate-800">
              {STAT_LABELS.map(({ key, label }) => (
                <div key={key}>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
                  <p className="font-medium tabular-nums">{run.stats![key]}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** Admin-only: the URL to register as the team webhook in Coolify's UI. */
function WebhookCard() {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const hook = useQuery({
    queryKey: ['sync', 'hook'],
    queryFn: () => api.get<SyncHookInfoDto>('/api/sync/hook'),
  });
  const rotate = useMutation({
    mutationFn: () => api.post<SyncHookInfoDto>('/api/sync/hook/rotate'),
    onSuccess: (info) => {
      queryClient.setQueryData(['sync', 'hook'], info);
    },
  });

  const url = hook.data ? (hook.data.url ?? `${window.location.origin}${hook.data.path}`) : '';

  return (
    <Card>
      <CardHeader
        title="Instant sync webhook"
        description={
          <>
            Optional: register this URL as the team webhook in Coolify's UI (Settings → Webhooks)
            to trigger a sync on Coolify events. See{' '}
            <HelpLink topic="sync/overview">Resource sync &amp; caching</HelpLink>.
          </>
        }
        actions={
          <Button
            variant="secondary"
            loading={rotate.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Rotate the webhook secret? The URL currently registered in Coolify stops working.',
                )
              ) {
                rotate.mutate();
              }
            }}
          >
            Rotate secret
          </Button>
        }
      />
      <div className="px-5 py-4">
        {hook.isPending ? (
          <Spinner />
        ) : hook.error ? (
          <ErrorNote error={hook.error} />
        ) : (
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg bg-slate-100 px-3 py-2 text-xs dark:bg-slate-800">
              {url}
            </code>
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(url).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                });
              }}
            >
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}
        {rotate.error != null && (
          <div className="mt-3">
            <ErrorNote error={rotate.error} />
          </div>
        )}
      </div>
    </Card>
  );
}

function FindingsCard() {
  const queryClient = useQueryClient();
  const findings = useQuery({
    queryKey: ['sync', 'findings'],
    queryFn: () => api.get<DriftFindingDto[]>('/api/sync/findings'),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['sync'] });
    void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
  };
  const adopt = useMutation({
    mutationFn: (id: string) => api.post(`/api/sync/findings/${id}/adopt`, {}),
    onSuccess: invalidate,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.post(`/api/sync/findings/${id}/dismiss`),
    onSuccess: invalidate,
  });

  return (
    <Card>
      <CardHeader
        title="Drift findings"
        description="Resources found in panel-managed Coolify projects without a ledger entry. Adopt to track them (plan limits apply) or dismiss to suppress."
      />
      {findings.isPending ? (
        <Spinner />
      ) : findings.error ? (
        <div className="p-5">
          <ErrorNote error={findings.error} />
        </div>
      ) : findings.data.length === 0 ? (
        <EmptyState
          icon={<SearchCheck className="size-10" />}
          title="No drift"
          description="Everything in panel-managed projects is accounted for in the ledger."
        />
      ) : (
        <div className="overflow-x-auto">
          {(adopt.error ?? dismiss.error) != null && (
            <div className="px-5 pt-4">
              <ErrorNote error={adopt.error ?? dismiss.error} />
            </div>
          )}
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3 font-medium">Resource</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Subscription</th>
                <th className="px-5 py-3 font-medium">Coolify status</th>
                <th className="px-5 py-3 font-medium">Last seen</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {findings.data.map((finding) => (
                <tr key={finding.id} className={finding.status === 'dismissed' ? 'opacity-60' : ''}>
                  <td className="px-5 py-3">
                    <p className="font-medium">{finding.name}</p>
                    <code className="text-xs text-slate-400">{finding.coolifyUuid}</code>
                  </td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
                    {finding.kind ?? finding.resourceType}
                  </td>
                  <td className="px-5 py-3">
                    {finding.subscriptionId ? (
                      <Link
                        to={`/subscriptions/${finding.subscriptionId}`}
                        className="text-brand-600 hover:underline dark:text-brand-400"
                      >
                        {finding.subscriptionName}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {finding.clientName && (
                      <p className="text-xs text-slate-500 dark:text-slate-400">{finding.clientName}</p>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {finding.lastStatus ? (
                      <Badge tone={statusTone(finding.lastStatus)}>{finding.lastStatus}</Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                    {new Date(finding.lastSeenAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3">
                    {finding.status === 'dismissed' ? (
                      <Badge tone="gray">dismissed</Badge>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="secondary"
                          loading={adopt.isPending && adopt.variables === finding.id}
                          disabled={!finding.kind || !finding.subscriptionId}
                          title={
                            !finding.kind
                              ? 'This Coolify type maps to no panel resource kind'
                              : undefined
                          }
                          onClick={() => adopt.mutate(finding.id)}
                        >
                          Adopt
                        </Button>
                        <Button
                          variant="ghost"
                          loading={dismiss.isPending && dismiss.variables === finding.id}
                          onClick={() => dismiss.mutate(finding.id)}
                        >
                          Dismiss
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function OrphansCard() {
  const queryClient = useQueryClient();
  const orphans = useQuery({
    queryKey: ['sync', 'orphans'],
    queryFn: () => api.get<OrphanedResourceDto[]>('/api/sync/orphans'),
  });
  const remove = useMutation({
    mutationFn: (orphan: OrphanedResourceDto) =>
      api.delete(`/api/subscriptions/${orphan.subscriptionId}/resources/${orphan.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sync'] });
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  return (
    <Card>
      <CardHeader
        title="Orphaned resources"
        description="Ledger entries whose Coolify resource no longer exists. They keep counting toward limits until removed — removal is your call, never automatic."
      />
      {orphans.isPending ? (
        <Spinner />
      ) : orphans.error ? (
        <div className="p-5">
          <ErrorNote error={orphans.error} />
        </div>
      ) : orphans.data.length === 0 ? (
        <EmptyState
          icon={<Ghost className="size-10" />}
          title="No orphans"
          description="Every ledger entry still matches a live Coolify resource."
        />
      ) : (
        <div className="overflow-x-auto">
          {remove.error != null && (
            <div className="px-5 pt-4">
              <ErrorNote error={remove.error} />
            </div>
          )}
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3 font-medium">Resource</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Subscription</th>
                <th className="px-5 py-3 font-medium">Orphaned since</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {orphans.data.map((orphan) => (
                <tr key={orphan.id}>
                  <td className="px-5 py-3">
                    <Link
                      to={`/${orphan.kind}s/${orphan.id}`}
                      className="font-medium text-brand-600 hover:underline dark:text-brand-400"
                    >
                      {orphan.name}
                    </Link>
                    {orphan.coolifyUuid && (
                      <p>
                        <code className="text-xs text-slate-400">{orphan.coolifyUuid}</code>
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3 capitalize text-slate-600 dark:text-slate-300">
                    {orphan.kind}
                  </td>
                  <td className="px-5 py-3">
                    <Link
                      to={`/subscriptions/${orphan.subscriptionId}`}
                      className="text-brand-600 hover:underline dark:text-brand-400"
                    >
                      {orphan.subscriptionName}
                    </Link>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{orphan.clientName}</p>
                  </td>
                  <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                    {new Date(orphan.orphanedAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button
                      variant="danger"
                      loading={remove.isPending && remove.variables?.id === orphan.id}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove "${orphan.name}" from the ledger? This frees its plan-limit slot. Nothing is touched in Coolify.`,
                          )
                        ) {
                          remove.mutate(orphan);
                        }
                      }}
                    >
                      Remove from ledger
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function SyncPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['sync', 'status'],
    queryFn: () => api.get<SyncStatusDto>('/api/sync/status'),
    refetchInterval: (query) => (query.state.data?.running ? 2_000 : false),
  });
  const runNow = useMutation({
    mutationFn: () => api.post<{ coalesced: boolean; run: SyncRunDto | null }>('/api/sync/run'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sync'] });
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  return (
    <div>
      <PageHeader
        title="Sync"
        description="Keeps the resources ledger truthful: statuses, drift in both directions, and subscription health."
        actions={
          <Button loading={runNow.isPending} onClick={() => runNow.mutate()}>
            <RefreshCw className="size-4" /> Sync now
          </Button>
        }
      />
      {runNow.error != null && (
        <div className="mb-4">
          <ErrorNote error={runNow.error} />
        </div>
      )}

      <div className="space-y-4">
        {status.isPending ? (
          <Spinner />
        ) : status.error ? (
          <ErrorNote error={status.error} />
        ) : (
          <LastRunCard status={status.data} />
        )}
        <FindingsCard />
        <OrphansCard />
        {me.data?.role === 'admin' && (
          <>
            <WebhookCard />
            <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <Webhook className="size-3.5" />
              Coolify has no CRUD webhooks — the panel reconciles by diffing the full resource list
              every interval; the webhook only makes it immediate.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
